"use client";

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Footer from "./Footer";
import ERPLeadBot from "./ERPLeadBot";
import {
  Bus,
  CheckCircle2,
  MapPin,
  Bell,
  ShieldCheck,
  Smartphone,
  BarChart3,
  Users,
  Route,
  Truck,
  AlertTriangle,
  Clock,
  MessageSquare,
} from "lucide-react";

const features = [
  {
    icon: MapPin,
    title: "Live GPS Tracking",
    description:
      "Track every bus on a live map with 10-second location updates. Know where every vehicle is, always.",
  },
  {
    icon: Route,
    title: "Route & Stop Management",
    description:
      "Plan, assign, and optimise routes and stops with a simple drag-and-drop editor.",
  },
  {
    icon: Bell,
    title: "Parent Notifications",
    description:
      "Parents get automatic SMS alerts when the bus leaves, reaches their stop, and arrives at school.",
  },
  {
    icon: Smartphone,
    title: "Parent Mobile App",
    description:
      "Parents track the bus live, see ETA, and get push notifications, on Android and iOS.",
  },
  {
    icon: Truck,
    title: "Vehicle Records",
    description:
      "Maintain complete records: documents, insurance, maintenance history, and fitness certificates.",
  },
  {
    icon: Users,
    title: "Driver Management",
    description:
      "Manage driver profiles, licenses, duty schedules, and performance in one place.",
  },
  {
    icon: AlertTriangle,
    title: "Maintenance Alerts",
    description:
      "Automated reminders for service due dates, insurance renewals, and fitness certificate expiry.",
  },
  {
    icon: MapPin,
    title: "Geo-Fencing",
    description:
      "Set virtual zones around stops and schools, get alerts whenever a vehicle enters or exits.",
  },
  {
    icon: BarChart3,
    title: "Fleet Reports",
    description:
      "Detailed reports on fuel usage, trip history, driver performance, and maintenance costs.",
  },
];

const benefits = [
  "Real-time visibility over every vehicle in your fleet",
  "Reduce parent calls with automated SMS alerts",
  "Optimise routes to cut fuel costs",
  "Track maintenance schedules and never miss a renewal",
  "Android & iOS apps for parents and drivers",
  "Scales from 1 bus to 1000+ vehicles",
];

export default function BusManagement() {
  return (
    <div className="w-full">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-700 via-green-600 to-green-500 py-20 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 border border-white/20 text-white text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              <Bus className="w-4 h-4" /> Bus Management System
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Real-Time GPS Tracking for{" "}
              <span className="text-green-200">School & Corporate Fleets</span>
            </h1>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Know where every bus is at all times. Keep parents informed
              automatically and manage your entire fleet from one dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?product=bus">
                <Button size="lg" className="bg-white text-green-700 hover:bg-green-50">
                  Start Free Trial
                </Button>
              </Link>
              <Link to="/schedule-demo">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  Book a Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything Your Fleet Needs
            </h2>
            <p className="text-xl text-gray-500 max-w-xl mx-auto">
              From live GPS to maintenance alerts, manage every aspect of your
              bus operations.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <Card
                key={idx}
                className="border hover:border-green-200 transition-colors"
              >
                <CardContent className="p-6">
                  <div className="w-11 h-11 bg-green-50 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Fleet Operators Choose Clynox
              </h2>
              <p className="text-lg text-gray-500 mb-8">
                Schools and corporate fleets across India use Clynox Bus to
                improve safety, reduce costs, and keep parents informed
                automatically.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { value: "10s", label: "Location Refresh Rate" },
                { value: "100%", label: "Parent Alert Delivery" },
                { value: "30%", label: "Avg Fuel Savings" },
                { value: "24/7", label: "Live Tracking" },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 text-center shadow-sm border"
                >
                  <div className="text-3xl font-bold text-green-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-green-600 to-green-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Track Your Fleet Live?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Get started with a free 30-day trial. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?product=bus">
              <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100">
                Start Free Trial
              </Button>
            </Link>
            <Link to="/contact?product=bus">
              <Button
                size="lg"
                variant="outline"
                className="border-white bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                Contact Sales
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ERPLeadBot />
    </div>
  );
}
